import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GuideInputSelectStyle } from 'components/admin/input/GuideInputSelect/GuideInputSelectStyle';
import {
    WorldMapStories,
    WorldMapStoriesVariables
} from 'graphql/query/__generated__/WorldMapStories';
import { WORLD_MAP_STORIES } from 'graphql/query/worldQuery';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { EFonts } from 'libs/styles/styling';
import { normalizeStringForSearch } from 'libs/utils/text/normalizeStringForSearch';
import { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Value {
    label: string;
    value: string;
}

interface Props {
    worldMapId: string;
    excludeStoryIds?: string[];
    excludeExternalStoryIds?: boolean;
    onStorySelected?: (storyId: string) => void;
    className?: string;
    noOptionsMessage?: React.ReactNode;
}

export const StorySearchInput = (props: Props): JSX.Element => {
    const [textValue, setTextValue] = useState('');
    const { getTokenForMapId } = useWorldMapLocalStorage();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ref = useRef<any>(null);

    const [isFocused, setIsFocused] = useState(false);

    const query = useQuery<WorldMapStories, WorldMapStoriesVariables>(WORLD_MAP_STORIES, {
        variables: {
            worldMapId: props.worldMapId,
            token: getTokenForMapId(props.worldMapId)
        }
    });

    useEffect(() => {
        if (isFocused) {
            query.refetch();
        }
    }, [isFocused]);

    let items = (query.data?.publishedStoriesForWorldMap ?? [])
        .filter((story) => {
            return normalizeStringForSearch(story.header.title).includes(
                normalizeStringForSearch(textValue)
            );
        })
        .map((e) => ({ label: e.header?.title ?? Localization.NO_TITLE, value: e.storyId }));

    items = items.filter((e) => {
        if (props.excludeExternalStoryIds) {
            if (e.value.startsWith('external_p_story:')) {
                return false;
            }
        }
        return !props.excludeStoryIds?.includes(e.value);
    });

    return (
        <S_StorySearchInput className={props.className}>
            <S_Icon
                icon={['fal', 'magnifying-glass']}
                onClick={() => {
                    ref.current?.focus();
                }}
            />
            <S_Container>
                <S_Select
                    ref={ref}
                    value={null}
                    onFocus={() => {
                        setIsFocused(true);
                    }}
                    onInputChange={(value, action) => {
                        if (action.action === 'menu-close') {
                            setTextValue(action.prevInputValue);
                            return;
                        }
                        setTextValue(value);
                    }}
                    onChange={async (newValue: Value) => {
                        props.onStorySelected?.(newValue.value);
                        setTextValue('');
                    }}
                    openMenuOnFocus={false}
                    options={items}
                    isLoading={query.loading}
                    noOptionsMessage={() => {
                        if (items.length === 0) {
                            return <>{props.noOptionsMessage ?? Localization.NO_OPTIONS}</>;
                        }
                    }}
                    loadingMessage={() => {
                        return Localization.LOADING + '...';
                    }}
                    styles={GuideInputSelectStyle({ hideIcon: false })}
                    placeholder={Localization.SEARCH_DETAILS}
                    components={{
                        ClearIndicator: () => null,
                        DropdownIndicator: () => null
                    }}
                />
            </S_Container>
        </S_StorySearchInput>
    );
};

const S_StorySearchInput = styled.div`
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    z-index: 1000;
`;

const S_Icon = styled(FontAwesomeIcon)`
    color: #141c54;
    font-size: 20px;
    width: 20px;
    left: 18px;
    position: absolute;
    z-index: 1;
    vertical-align: middle;
    cursor: pointer;
`;

const S_Container = styled.div`
    width: 100%;
    font-size: 14px;
    font-family: ${EFonts.Regular};
    border-radius: 7px;
`;

const S_Select = styled(Select<Value>)`
    width: 100%;
`;
